import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { safeSaveText } from '@/lib/utils';

interface MobileItemEditorProps {
  isOpen: boolean;
  onClose: () => void;
  itemText: string;
  notes: string;
  onSave: (itemText: string, notes: string) => void;
  focusField?: 'item' | 'notes';
}

export const MobileItemEditor = ({ 
  isOpen, 
  onClose, 
  itemText, 
  notes, 
  onSave,
  focusField = 'item'
}: MobileItemEditorProps) => {
  const [editedItemText, setEditedItemText] = useState(itemText);
  const [editedNotes, setEditedNotes] = useState(notes);
  const itemInputRef = useRef<HTMLTextAreaElement>(null);
  const notesTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync internal state with props when they change
  useEffect(() => {
    setEditedItemText(itemText);
    setEditedNotes(notes);
  }, [itemText, notes]);

  // Focus the correct field when dialog opens
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        if (focusField === 'notes') {
          notesTextareaRef.current?.focus();
        } else {
          itemInputRef.current?.focus();
        }
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen, focusField]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setEditedItemText(itemText);
      setEditedNotes(notes);
    }
  }, [isOpen, itemText, notes]);

  const handleSave = () => {
    onSave(safeSaveText(editedItemText, 60), safeSaveText(editedNotes, 60));
    onClose();
  };

  const handleCancel = () => {
    setEditedItemText(itemText);
    setEditedNotes(notes);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className="max-w-6xl max-h-[90vh] md:max-h-[90vh] min-h-0 md:min-h-[600px] flex flex-col p-0 overflow-y-auto">
        <div className="flex flex-col h-full bg-background">
          {/* Header */}
          <div className="flex items-center justify-center p-4 border-b bg-muted/50">
            <h2 className="text-lg font-normal text-foreground">Edit Item</h2>
          </div>

          {/* Content */}
          <div className="flex-1 p-4 space-y-6">
            <div className="space-y-2">
              <label htmlFor="item-text" className="text-sm font-normal text-foreground">
                Item Description
              </label>
              <Textarea
                id="item-text"
                ref={itemInputRef}
                value={editedItemText}
                onChange={(e) => setEditedItemText(e.target.value)}
                placeholder="Enter item description..."
                className="text-base px-4 py-3 resize-none"
                maxLength={60}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="notes" className="text-sm font-normal text-foreground">
                Notes
              </label>
              <Textarea
                id="notes"
                ref={notesTextareaRef}
                value={editedNotes}
                onChange={(e) => setEditedNotes(e.target.value)}
                placeholder="Add any notes..."
                className="text-base px-4 py-3 resize-none"
                maxLength={60}
                rows={2}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t bg-muted/50 space-y-3">
            <Button
              onClick={handleSave}
              variant="accent"
              className="w-full h-12 text-base"
            >
              Save Changes
            </Button>
            <Button
              onClick={handleCancel}
              variant="outline"
              className="w-full h-12 text-base"
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};